// ─── LayoutVersionPanel ──────────────────────────────────────
// Phase 1: Section-v1 mobile-parity opt-in + safe rollback.
//
// Shown inside Event Setup → Digital Invitation tab (v2 engine).
// Owners can flip the public renderer to the new section-v1 (% + anchor)
// layout to fix mobile drift, and revert any time — a backup of the
// pre-migration content is captured automatically on first opt-in.

import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import PreviewOnPhoneButton from './PreviewOnPhoneButton';
import MobileHealthCheckButton from './MobileHealthCheckButton';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LayoutVersionPanel({ eventId, rsvpPageInfo, onUpdated, onSelectElement, adminDarkMode }) {
  const [busy, setBusy] = useState(false);
  const isV1 = rsvpPageInfo?.layout_version === 'section-v1';
  const hasBackup = !!rsvpPageInfo?.v2_content_backup_at;

  const callMigrate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/migrate-to-section-v1`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      toast.success('Mobile-parity layout enabled. Open your public link to confirm.');
      onUpdated?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setBusy(false);
    }
  };

  const callRestore = async () => {
    if (busy) return;
    if (!window.confirm('Restore the previous layout? Your invitation will go back to the original (legacy) renderer.')) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/restore-backup`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      toast.success('Previous layout restored.');
      onUpdated?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`p-4 rounded-xl border ${adminDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}
      data-testid="layout-version-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h4 className={`text-xs font-medium ${adminDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Mobile-parity layout
              <span
                className={`ml-2 px-1.5 py-0.5 text-[10px] rounded-full uppercase tracking-wide ${
                  isV1
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
                data-testid="layout-version-badge"
              >
                {isV1 ? 'section-v1' : 'legacy'}
              </span>
            </h4>
          </div>
          <p className={`text-xs mt-1 ${adminDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {isV1
              ? 'Element positions scale with viewport width so the public page matches the editor preview on real phones.'
              : 'Try the new layout engine to fix element drift between the editor preview and real phones. Reversible — we keep a backup.'}
          </p>
          {hasBackup && (
            <p className={`text-[10px] mt-1 ${adminDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Backup taken {new Date(rsvpPageInfo.v2_content_backup_at).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <PreviewOnPhoneButton
            slug={rsvpPageInfo?.slug}
            layoutVersion={rsvpPageInfo?.layout_version}
            layoutMode={rsvpPageInfo?.layout_mode}
            updatedAt={rsvpPageInfo?.updated_at}
            isPublished={rsvpPageInfo?.is_published}
            previewToken={rsvpPageInfo?.preview_token}
            adminDarkMode={adminDarkMode}
          />
          <MobileHealthCheckButton
            slug={rsvpPageInfo?.slug}
            eventId={eventId}
            layoutVersion={rsvpPageInfo?.layout_version}
            layoutMode={rsvpPageInfo?.layout_mode}
            isPublished={rsvpPageInfo?.is_published}
            previewToken={rsvpPageInfo?.preview_token}
            onLayoutVersionChange={(v) => setRsvpPageInfo((prev) => ({ ...prev, layout_version: v }))}
            onSelectElement={onSelectElement}
            variant="panel"
            adminDarkMode={adminDarkMode}
          />
          {!isV1 && (
            <button
              onClick={callMigrate}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap"
              data-testid="layout-migrate-btn"
            >
              {busy ? 'Enabling…' : 'Enable mobile-parity layout'}
            </button>
          )}
          {isV1 && hasBackup && (
            <button
              onClick={callRestore}
              disabled={busy}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                adminDarkMode
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="layout-restore-btn"
            >
              {busy ? 'Restoring…' : 'Restore previous layout'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
